import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Clock, AlertTriangle } from 'lucide-react';

export default function PricingTiers() {
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchUserCount() {
      try {
        setLoading(true);
        
        // The cutoff date - May 29th, 2025 (same as in RecentUsersStats)
        const cutoffDate = '2025-05-29T00:00:00Z';
        
        // Get count of users who joined before the cutoff date
        const { count: beforeCutoffCount, error: beforeError } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .lt('created_at', cutoffDate);
          
        if (beforeError) throw beforeError;
        
        // Get count of paid subscribers who joined on or after the cutoff date
        const { count: paidAfterCutoffCount, error: paidError } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', cutoffDate)
          .eq('is_subscribed', true);
          
        if (paidError) throw paidError;
        
        // Use the same calculation as RecentUsersStats
        if (beforeCutoffCount !== null && paidAfterCutoffCount !== null) {
          const displayCount = beforeCutoffCount + paidAfterCutoffCount;
          setTotalUsers(displayCount);
          console.log('PricingTiers - Total users count:', displayCount, 
            '(Before cutoff:', beforeCutoffCount, 
            '+ Paid after cutoff:', paidAfterCutoffCount, ')');
        }
      } catch (error) {
        console.error('Error fetching user count:', error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchUserCount();
  }, []);

  // Calculate remaining slots for current tier
  const getCurrentTier = () => {
    if (totalUsers < 500) {
      return {
        name: 'Free Tier',
        price: 'FREE',
        remaining: 500 - totalUsers,
        isCurrent: true,
        nextPrice: '$9.99'
      };
    } else if (totalUsers < 1000) {
      return {
        name: 'Early Adopter',
        price: '$9.99',
        remaining: 1000 - totalUsers,
        isCurrent: true,
        nextPrice: '$19.99'
      };
    } else if (totalUsers < 1500) {
      return {
        name: 'Standard Tier',
        price: '$19.99',
        remaining: 1500 - totalUsers,
        isCurrent: true,
        nextPrice: '$29.99'
      };
    } else {
      return {
        name: 'Premium Tier',
        price: '$29.99',
        remaining: 0,
        isCurrent: true,
        nextPrice: null
      };
    }
  };

  const currentTier = getCurrentTier();
  const percentFilled = currentTier.remaining === 0 
    ? 100 
    : currentTier.name === 'Free Tier' 
      ? Math.round((totalUsers / 500) * 100)
      : currentTier.name === 'Early Adopter'
        ? Math.round(((totalUsers - 500) / 500) * 100) 
        : Math.round(((totalUsers - 1000) / 500) * 100);

  // Determine if close to next tier (less than 10% spots remaining)
  const isCloseToNextTier = currentTier.remaining > 0 && 
    (currentTier.remaining / (currentTier.name === 'Free Tier' ? 500 : currentTier.name === 'Early Adopter' ? 500 : 500)) < 0.1;

  return (
    <div className="mt-1 mb-4">
      {loading ? (
        <div className="flex items-center justify-center h-12 text-gray-400">
          <div className="w-4 h-4 border-2 border-t-transparent border-[#c9fffc] rounded-full animate-spin mr-2"></div>
          Loading pricing...
        </div>
      ) : (
        <div className="rounded-lg overflow-hidden bg-gray-800/50 backdrop-blur-sm border border-[#c9fffc]/10 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-[#c9fffc]" />
              <h3 className="text-sm font-medium text-white">Limited Time Pricing</h3>
            </div>
            
            {isCloseToNextTier && (
              <div className="flex items-center text-amber-400 text-xs gap-1">
                <AlertTriangle className="w-3 h-3" />
                <span>Almost full!</span>
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <div className="relative h-2 bg-gray-700 rounded overflow-hidden">
              <div 
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#c9fffc] to-[#a0fcf9]" 
                style={{ width: `${percentFilled}%` }}
              ></div>
            </div>
            
            <div className="flex justify-between text-xs">
              <span className="text-white font-medium">Current price: <span className="text-[#c9fffc]">{currentTier.price}</span></span>
              {currentTier.remaining > 0 && (
                <span className="text-gray-400">
                  <span className="text-amber-400 font-medium">{currentTier.remaining}</span> spots left before {currentTier.nextPrice}
                </span>
              )}
            </div>
          </div>
          
          <div className="mt-3 grid grid-cols-4 gap-1 text-center text-xs">
            <div className={`p-1 rounded ${totalUsers < 500 ? 'bg-[#c9fffc]/10 text-white' : 'text-gray-500'}`}>
              <div className="font-medium">0-500</div>
              <div>FREE</div>
            </div>
            <div className={`p-1 rounded ${totalUsers >= 500 && totalUsers < 1000 ? 'bg-[#c9fffc]/10 text-white' : 'text-gray-500'}`}>
              <div className="font-medium">500-1000</div>
              <div>$9.99</div>
            </div>
            <div className={`p-1 rounded ${totalUsers >= 1000 && totalUsers < 1500 ? 'bg-[#c9fffc]/10 text-white' : 'text-gray-500'}`}>
              <div className="font-medium">1000-1500</div>
              <div>$19.99</div>
            </div>
            <div className={`p-1 rounded ${totalUsers >= 1500 ? 'bg-[#c9fffc]/10 text-white' : 'text-gray-500'}`}>
              <div className="font-medium">1500+</div>
              <div>$29.99</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
